import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  ShoppingCart, 
  Users, 
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Target,
  MessageSquare,
  CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { adminAPI } from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdvisor = user?.role === 'ADVISOR';
  const isSupport = user?.role === 'SUPPORT';

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await adminAPI.getDashboard(30);
      setData(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-secondary animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-secondary animate-pulse rounded-lg" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-80 bg-secondary animate-pulse rounded-lg" />
          <div className="h-80 bg-secondary animate-pulse rounded-lg" />
        </div>
      </div>
    );
  }

  // Dashboard for Advisors (Conseillers)
  if (isAdvisor) {
    const advisorKpis = [
      {
        title: 'Mes ventes du mois',
        value: formatPrice(data?.total_revenue_cents || 0),
        icon: DollarSign,
        trend: '+12%',
        trendUp: true,
        color: 'text-emerald-500'
      },
      {
        title: 'Mes commandes',
        value: data?.orders_count || 0,
        icon: ShoppingCart,
        trend: '+8%',
        trendUp: true,
        color: 'text-blue-500'
      },
      {
        title: 'Commission estimée',
        value: formatPrice((data?.total_revenue_cents || 0) * 0.1), // 10% commission
        icon: Target,
        trend: '+12%',
        trendUp: true,
        color: 'text-purple-500'
      },
      {
        title: 'Messages en attente',
        value: data?.pending_messages || 0,
        icon: MessageSquare,
        color: 'text-orange-500'
      }
    ];

    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Mon tableau de bord</h1>
          <p className="text-muted-foreground">Bienvenue {user?.first_name}, voici vos performances du mois</p>
        </div>

        {/* Advisor KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {advisorKpis.map((kpi, index) => (
            <motion.div
              key={kpi.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {kpi.title}
                  </CardTitle>
                  <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono">{kpi.value}</div>
                  {kpi.trend && (
                    <div className="flex items-center text-xs mt-1">
                      {kpi.trendUp ? (
                        <ArrowUpRight className="h-3 w-3 text-emerald-500 mr-1" />
                      ) : (
                        <ArrowDownRight className="h-3 w-3 text-red-500 mr-1" />
                      )}
                      <span className={kpi.trendUp ? 'text-emerald-500' : 'text-red-500'}>
                        {kpi.trend}
                      </span>
                      <span className="text-muted-foreground ml-1">vs mois dernier</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Advisor Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Objectifs du mois</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Ventes mensuelles</span>
                  <span className="text-sm text-muted-foreground">
                    {formatPrice(data?.total_revenue_cents || 0)} / {formatPrice(1500000)}
                  </span>
                </div>
                <div className="w-full bg-secondary h-2 rounded-full">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(100, ((data?.total_revenue_cents || 0) / 1500000) * 100)}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Commandes clôturées</span>
                  <span className="text-sm text-muted-foreground">
                    {data?.orders_count || 0} / 50
                  </span>
                </div>
                <div className="w-full bg-secondary h-2 rounded-full">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(100, ((data?.orders_count || 0) / 50) * 100)}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Activité récente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { label: 'Conversations actives', value: data?.active_conversations || 0, icon: MessageSquare },
                  { label: 'Commandes en cours', value: data?.orders_by_status?.PROCESSING || 0, icon: ShoppingCart },
                  { label: 'Commandes livrées', value: data?.orders_by_status?.DELIVERED || 0, icon: CheckCircle2 }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <item.icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{item.label}</span>
                    </div>
                    <span className="font-mono font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Dashboard for Support
  if (isSupport) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Support Dashboard</h1>
          <p className="text-muted-foreground">Gérez les conversations et supportez vos clients</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Messages en attente
              </CardTitle>
              <MessageSquare className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">{data?.pending_messages || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Conversations actives
              </CardTitle>
              <Users className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">{data?.active_conversations || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Résolues aujourd'hui
              </CardTitle>
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">{data?.resolved_today || 0}</div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Commandes récentes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Accédez à l'onglet Commandes pour voir toutes les commandes
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin/Super Admin Dashboard (existing)
  const kpiCards = [
    {
      title: 'Chiffre d\'affaires',
      value: formatPrice(data?.total_revenue_cents || 0),
      icon: DollarSign,
      trend: '+12%',
      trendUp: true,
      color: 'text-emerald-500'
    },
    {
      title: 'Commandes',
      value: data?.orders_count || 0,
      icon: ShoppingCart,
      trend: '+8%',
      trendUp: true,
      color: 'text-blue-500'
    },
    {
      title: 'Panier moyen',
      value: formatPrice(data?.average_order_value_cents || 0),
      icon: TrendingUp,
      trend: '+5%',
      trendUp: true,
      color: 'text-purple-500'
    },
    {
      title: 'Leads',
      value: data?.leads_count || 0,
      icon: Users,
      trend: '+15%',
      trendUp: true,
      color: 'text-orange-500'
    }
  ];

  // Format revenue data for chart
  const revenueData = data?.revenue_by_day?.map(d => ({
    date: new Date(d.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
    revenue: d.revenue / 100,
    orders: d.orders
  })) || [];

  // Orders by status for bar chart
  const statusData = data?.orders_by_status ? Object.entries(data.orders_by_status)
    .filter(([_, count]) => count > 0)
    .map(([status, count]) => ({
      status: status.replace('_', ' '),
      count
    })) : [];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Vue d'ensemble des 30 derniers jours</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi, index) => (
          <motion.div
            key={kpi.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {kpi.title}
                </CardTitle>
                <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">{kpi.value}</div>
                <div className="flex items-center text-xs mt-1">
                  {kpi.trendUp ? (
                    <ArrowUpRight className="h-3 w-3 text-emerald-500 mr-1" />
                  ) : (
                    <ArrowDownRight className="h-3 w-3 text-red-500 mr-1" />
                  )}
                  <span className={kpi.trendUp ? 'text-emerald-500' : 'text-red-500'}>
                    {kpi.trend}
                  </span>
                  <span className="text-muted-foreground ml-1">vs mois dernier</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Revenus (7 derniers jours)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="date" 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      tickFormatter={(v) => `${v}€`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [`${value}€`, 'Revenue']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="hsl(var(--accent))" 
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Orders by Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Commandes par statut</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="status" 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={10}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="hsl(var(--accent))" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Statistiques rapides</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Taux de conversion</p>
                <p className="text-xl font-bold font-mono">{data?.conversion_rate?.toFixed(1) || 0}%</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Taux de remboursement</p>
                <p className="text-xl font-bold font-mono">{data?.refund_rate?.toFixed(1) || 0}%</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Commandes en attente</p>
                <p className="text-xl font-bold font-mono">
                  {data?.orders_by_status?.PENDING_PAYMENT || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">À expédier</p>
                <p className="text-xl font-bold font-mono">
                  {data?.orders_by_status?.PAID || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
